import { useScopeSegmentsSection } from "@domains/segments/client/components/scope/useScopeSegmentsSection.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { ScopeSelector } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAnalyticsScopeClient } from "../api/query.ts";
import { AnalyticsFilterDimensions, useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import type { ReportingScope } from "@packages/core/organization/scope.ts";
import { type FeedbackPeriod, getFeedbackDataFloor } from "../hooks/feedbackPeriod.ts";
import { useFeedbackPeriod, useFeedbackScope, useFeedbackSegment, useIsFeedbackSegmentEnabled } from "../hooks/useFeedbackFilters.ts";
import { FeedbackPeriodToolbar } from "./FeedbackPeriodToolbar.tsx";
import { FeedbackFilterBar } from "./FeedbackFilterBar.tsx";

export function FeedbackFiltersHeading() {
    const [scope, setScope] = useFeedbackScope();
    const [segmentId, setSegmentId] = useFeedbackSegment();
    const [period, setPeriod] = useFeedbackPeriod(scope);
    const isSegmentEnabled = useIsFeedbackSegmentEnabled();
    const segments = useScopeSegmentsSection({ isEnabled: isSegmentEnabled, segmentId });
    const scopeClient = useAnalyticsScopeClient();
    const { trackTimeRangeApplied, trackFilterApplied, trackAudienceScopeChanged } = useAnalyticsTracking();

    const { data: previews, isPending } = useQuery(createQueryOptions(scopeClient.getPreviews.queryOptions(), { throwOnError: false }));

    const handlePeriodChange = useCallback(
        (next: FeedbackPeriod) => {
            setPeriod(next);
            trackTimeRangeApplied(next.kind === "preset" ? next.timeFrame : next.kind);
        },
        [setPeriod, trackTimeRangeApplied]
    );

    const handleScopeChange = useCallback(
        (next: ReportingScope) => {
            void setScope(next);
            trackAudienceScopeChanged(next);
        },
        [setScope, trackAudienceScopeChanged]
    );

    const handleSegmentChange = useCallback(
        (next: string | null) => {
            void setSegmentId(next);

            if (next) {
                void setScope(null);
                trackFilterApplied(AnalyticsFilterDimensions.Segment);
            }
        },
        [setSegmentId, setScope, trackFilterApplied]
    );

    return (
        <>
            <ScopeSelector
                scope={scope}
                onScopeChange={handleScopeChange}
                segmentId={segmentId}
                onSegmentChange={isSegmentEnabled ? handleSegmentChange : undefined}
                activeSegmentName={segments.activeSegmentName}
                previews={previews}
                isPreviewsPending={isPending}
                trailingContent={
                    <div className="flex flex-wrap items-center gap-2">
                        <FeedbackPeriodToolbar period={period} onPeriodChange={handlePeriodChange} dataFloor={getFeedbackDataFloor(scope)} />
                        <FeedbackFilterBar />
                    </div>
                }
                renderSegmentsSection={isSegmentEnabled ? segments.render : undefined}
                onOpenChange={isSegmentEnabled ? segments.onPopoverOpenChange : undefined}
            />
            {isSegmentEnabled && segments.liveRegion}
        </>
    );
}
